/**
 * V2TargetValueProvider
 *
 * Determines the value input configuration based on variable metadata and operator.
 * Unlike V1 which derived value options from screen structure,
 * V2 uses the variable's own metadata (options, min/max, etc.).
 *
 * @module v2-condition-editor
 */

import type { ComparisonOperator, VariableType } from '@segmently/schema-models/v2';
import type { V2DataSource, V2TargetValueOptions } from '../types/v2-condition-editor.types';
import { variableOperatorService } from '@modules/v2-variable-utils/services/VariableOperatorService';

export class V2TargetValueProvider {
  /**
   * Get value input configuration for a data source and operator.
   *
   * Rules:
   * - exists/notExists → no value input ('none')
   * - enum/enum[] with eq/neq → single select from variable options
   * - enum/enum[] with in/notIn/containsAll/containsAny → multi-select
   * - boolean → select with true/false options
   * - number → numeric text input with min/max
   * - date → date input with minDate/maxDate
   * - string → free text input
   *
   * @param dataSource - Variable-based data source with metadata
   * @param operator - Selected comparison operator
   * @returns Target value options for the UI
   */
  getValueOptions(
    dataSource: V2DataSource,
    operator: ComparisonOperator | string,
  ): V2TargetValueOptions {
    // exists/notExists need no value
    if (!variableOperatorService.operatorRequiresValue(operator as ComparisonOperator)) {
      return { inputType: 'none' };
    }

    const requiresMulti = variableOperatorService.operatorRequiresMultiValue(operator as ComparisonOperator);

    return this.getOptionsForType(dataSource, requiresMulti);
  }

  /**
   * Get value options based on variable type and multi-value flag.
   */
  private getOptionsForType(
    dataSource: V2DataSource,
    requiresMulti: boolean,
  ): V2TargetValueOptions {
    const { variableType } = dataSource;

    switch (variableType) {
      case 'enum':
      case 'enum[]': {
        const options = (dataSource.options ?? []).map((o) => ({
          value: o.id,
          label: o.label,
          description: o.id,
        }));
        return {
          inputType: requiresMulti ? 'multi-select' : 'select',
          options,
        };
      }

      case 'boolean':
        return {
          inputType: 'select',
          options: [
            { value: 'true', label: 'True' },
            { value: 'false', label: 'False' },
          ],
        };

      case 'number':
        return {
          inputType: 'number',
          placeholder: 'Enter a number',
          min: dataSource.min,
          max: dataSource.max,
        };

      case 'date':
        return {
          inputType: 'date',
          placeholder: 'Select a date',
          minDate: dataSource.minDate,
          maxDate: dataSource.maxDate,
        };

      case 'string':
      default:
        return {
          inputType: requiresMulti ? 'multi-select' : 'text',
          placeholder: 'Enter a value',
        };
    }
  }
}

/** Singleton instance */
export const v2TargetValueProvider = new V2TargetValueProvider();
